import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Map as RouteAnalysisIcon,
  Summarize as ResultsIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout,
  ChevronLeft,
  ChevronRight,
  Explore as ExploreIcon,
  Assignment as AssignmentIcon,
  Hub as NetworkIcon,
  Schedule as ScheduleIcon,
  BugReport as BugReportIcon,
  Timeline as TimelineIcon,
  ViewQuilt as ViewQuiltIcon,
  Shield as ShieldIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LayoutProvider } from '../../contexts/LayoutContext';
import { useFeatureFlagsStore } from '../../stores/featureFlagsStore';
import { useAccessControlStore, useEffectiveAccess } from '../../stores/accessControlStore';
import { FEATURE_FLAG_IDS } from '../../types/featureFlags';
import endurionWordmark from '../../assets/endurion-wordmark.png';

const CUI_BANNER_HEIGHT = 28;
/** Compact toolbar row under CUI (tabs + actions); keep in sync with AppBar Toolbar below */
const TOP_NAV_BAR_HEIGHT = 48;

const BUG_REPORT_MAILTO_BODY = [
  'Please describe the issue below.',
  '',
  '1. What were you trying to do?',
  '',
  '2. What happened instead (error message, unexpected behavior)?',
  '',
  '3. Which page / module (COP, Pathfinder, AdTech, etc.)?',
  '',
  '4. When did this occur (date/time and timezone)?',
  '',
  '5. Can you reproduce it? If yes, steps:',
  '   1) ',
  '   2) ',
  '   3) ',
  '',
  '6. Browser / device (Chrome, Edge, etc.)?',
  '',
  '7. Screenshot or screen recording attached? (yes/no)',
  '',
  '---',
  'Environment: Pathfinder Commercial',
  'URL: ',
].join('\n');

/** Opens the user's mail client with a Pathfinder bug-report template. */
const openBugReportEmail = () => {
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const href =
    `mailto:customersupport@quietprofessionalsllc.com` +
    `?subject=${encodeURIComponent('Pathfinder Bug Report')}` +
    `&body=${encodeURIComponent(BUG_REPORT_MAILTO_BODY + pageUrl)}`;
  window.location.href = href;
};

// Responsive drawer widths
const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_TABLET = 200;
const COLLAPSED_DRAWER_WIDTH = 48;
const COLLAPSED_DRAWER_WIDTH_TABLET = 48;

const allMenuItems = [
  { text: 'Command Center', icon: <HomeIcon />, path: '/dashboard' },
  { text: 'Analysis', icon: <RouteAnalysisIcon />, path: '/analysis' },
  { text: 'Scheduled Analysis', icon: <ScheduleIcon />, path: '/scheduled-analyses' },
  { text: 'Results', icon: <ResultsIcon />, path: '/results' },
  { text: 'COP Dashboard', icon: <DashboardIcon />, path: '/cop' },
  { text: 'Pathfinder', icon: <ExploreIcon />, path: '/pathfinder' },
  // Missions live in the Intel list alongside packets; /missions stays routable
  // for shared route-planner permalinks but is no longer its own nav entry.
  { text: 'Intel', icon: <FactCheckIcon />, path: '/intel' },
  { text: 'DFP / Digital Force Protection', icon: <ShieldIcon />, path: '/force-protection' },
  { text: 'My dashboards', icon: <ViewQuiltIcon />, path: '/content' },
  { text: 'Link Analysis', icon: <NetworkIcon />, path: '/link-analysis' },
  { text: 'Pattern of Life', icon: <TimelineIcon />, path: '/pattern-of-life' },
  { text: 'Itinerary', icon: <AssignmentIcon />, path: '/itinerary' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

function getMenuItemsForRole(user: any) {
  if (!user || !user.roles) return allMenuItems;
  
  // PF external guests: COP + Coordination Itinerary only (unless analyst or higher)
  const hasAnalystOrHigher = user.roles.some((r: string) =>
    ['administrator', 'user_access_administrator', 'analyst'].includes(r)
  );
  if (user.roles.includes('pf_external_guest') && !hasAnalystOrHigher) {
    return allMenuItems.filter(
      item => item.text === 'COP Dashboard' || item.text === 'Itinerary'
    );
  }

  // Students and trainees: Only Itinerary
  if (user.roles.includes('student')) {
    return allMenuItems.filter(item => item.text === 'Itinerary');
  }
  
  // Viewers and guests: No scheduled analysis access
  const hasOnlyViewerOrLess = user.roles.every((role: string) => 
    ['viewer', 'guest'].includes(role)
  );
  
  if (hasOnlyViewerOrLess) {
    return allMenuItems.filter(item => item.text !== 'Scheduled Analysis');
  }
  
  // Analysts and administrators: Full access
  return allMenuItems;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const theme = useTheme();
  
  // Responsive breakpoint for tablet layout
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'), { noSsr: true });
  
  const isPathEnabled = useFeatureFlagsStore((s) => s.isPathEnabled);
  const navPlacement = useFeatureFlagsStore((s) => s.navPlacement);
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const loadModuleSettings = useFeatureFlagsStore((s) => s.loadFromServer);
  // Subscribe to all flag values so the nav re-renders when toggles change
  const flagSnapshot = useFeatureFlagsStore((s) => 
    FEATURE_FLAG_IDS.map((id) => s[id]).join(',')
  );
  const effectiveAccess = useEffectiveAccess();
  const canPath = useAccessControlStore((s) => s.canPath);
  const isTopNav = navPlacement === 'top';
  const roleItems = getMenuItemsForRole(user);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- flagSnapshot / effectiveAccess force re-eval
  const menuItems = React.useMemo(() => roleItems
    .filter((item) => isPathEnabled(item.path))
    .filter((item) => item.path === '/settings' || canPath(item.path))
    .filter((item) => item.path !== '/content' || hasPermission('analyst')),
  [roleItems, isPathEnabled, flagSnapshot, hasPermission, canPath, effectiveAccess]);

  const navTabPath =
    menuItems.find(
      (m) =>
        location.pathname === m.path ||
        (m.path !== '/' && location.pathname.startsWith(`${m.path}/`)) ||
        (m.path === '/content' && location.pathname.startsWith('/dashboard-builder'))
    )?.path ?? false;
  
  const [mobileOpen, setMobileOpen] = useState(false);
  // Persist sidebar state in localStorage so it stays open/closed across navigation
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : true; // Default to collapsed
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Save sidebar state to localStorage when it changes
  React.useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    loadModuleSettings();
  }, [loadModuleSettings]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  // Calculate responsive drawer width
  const getDrawerWidth = () => {
    if (isTablet) {
      return sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH_TABLET : DRAWER_WIDTH_TABLET;
    }
    return sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;
  };

  const currentDrawerWidth = getDrawerWidth();

  const closeMobileNav = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const mobileNavList = (
    <List sx={{ py: 0 }}>
      {menuItems.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            selected={navTabPath === item.path}
            onClick={() => {
              navigate(item.path);
              if (isTopNav) closeMobileNav();
            }}
            sx={{
              minHeight: 48,
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
                '& .MuiListItemIcon-root': { color: 'white' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  const drawer = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo and Branding Header */}
      <Box sx={{ 
        p: sidebarCollapsed ? 0.5 : 2, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 1,
        bgcolor: '#03111E',
        position: 'relative',
        minHeight: sidebarCollapsed ? 48 : 'auto',
      }}>
        {/* Collapsed state: Just show expand button prominently */}
        {sidebarCollapsed ? (
          <Tooltip title="Expand Menu" placement="right">
            <IconButton 
              onClick={handleSidebarToggle}
              sx={{ 
                color: 'white',
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                width: 28,
                height: 28,
              }}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            {/* Endurion logo - only show when expanded */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                width: '100%',
                pr: 3,
              }}
            >
              <Box
                component="img"
                src={endurionWordmark}
                alt="Endurion"
                sx={{
                  width: 38,
                  height: 38,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <Typography
                component="span"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.35rem',
                  lineHeight: 1,
                  letterSpacing: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                ENDURION
              </Typography>
            </Box>

            {/* Collapse Toggle - only show when expanded */}
            <IconButton 
              onClick={handleSidebarToggle}
              size="small"
              sx={{ 
                color: 'white',
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <ChevronLeft />
            </IconButton>
          </>
        )}
      </Box>
      <Divider />
      
      {/* Navigation Menu - Scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={navTabPath === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: sidebarCollapsed ? 40 : 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  px: sidebarCollapsed ? 0 : 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarCollapsed ? 0 : 40,
                    width: sidebarCollapsed ? '100%' : 'auto',
                    mr: sidebarCollapsed ? 0 : 3,
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': { fontSize: sidebarCollapsed ? 20 : 22 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Divider />
      
      {/* User Profile & Actions at Bottom */}
      <Box sx={{ flexShrink: 0, p: sidebarCollapsed ? 0.5 : 2 }}>
        {/* Bug Report Button */}
        {!sidebarCollapsed ? (
          <Button
            fullWidth
            size="small"
            startIcon={<BugReportIcon />}
            onClick={openBugReportEmail}
            sx={{ 
              mb: 1,
              justifyContent: 'flex-start',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            Report Bug
          </Button>
        ) : (
          <Tooltip title="Report a Bug">
            <IconButton
              size="small"
              onClick={openBugReportEmail}
              sx={{ 
                mb: 1,
                width: '100%',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <BugReportIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        
        {/* User Profile */}
        {!sidebarCollapsed && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            mb: 1
          }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <AccountCircle />
            </Avatar>
            <Typography 
              variant="body2" 
              sx={{ 
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.username}
            </Typography>
          </Box>
        )}
        
        {sidebarCollapsed && (
          <Tooltip title={user?.username || 'User'}>
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{ 
                width: '100%',
                justifyContent: 'center',
                mb: 1
              }}
            >
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Tooltip>
        )}
        
        {/* Logout Button */}
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{ 
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            minWidth: sidebarCollapsed ? 0 : 'auto',
            px: sidebarCollapsed ? 1 : 2
          }}
        >
          {!sidebarCollapsed && 'Logout'}
        </Button>
        
        {/* Menu for collapsed state */}
        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled sx={{ opacity: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <AccountCircle />
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.username}
                </Typography>
                {user?.roles && user.roles.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {user.roles.join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={openBugReportEmail}>
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            Report Bug
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </div>
  );

  const bannerHeight =
    classificationBannerMode === 'commercial' ? 0 : CUI_BANNER_HEIGHT;
  const topChromeOffset = bannerHeight + TOP_NAV_BAR_HEIGHT;

  const bannerVisual = ((): {
    label: string;
    backgroundColor: string;
    color: string;
  } | null => {
    if (classificationBannerMode === 'commercial') return null;
    if (classificationBannerMode === 'unclassified') {
      return {
        label: 'UNCLASSIFIED',
        backgroundColor: 'rgb(0, 122, 51)',
        color: '#fff',
      };
    }
    return {
      label: 'Controlled Unclassified Information (CUI)',
      backgroundColor: '#7b2cc9',
      color: 'white',
    };
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />

      {/* Top classification banner (CUI / UNCLASSIFIED; hidden for Commercial) */}
      {bannerVisual && (
        <Box
          sx={{
            backgroundColor: bannerVisual.backgroundColor,
            color: bannerVisual.color,
            textAlign: 'center',
            py: 0.5,
            px: 2,
            zIndex: theme.zIndex.modal + 2,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            height: CUI_BANNER_HEIGHT,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#fff' }}
          >
            {bannerVisual.label}
          </Typography>
        </Box>
      )}

      {isTopNav ? (
        <>
          <AppBar
            position="fixed"
            elevation={2}
            sx={{
              top: bannerHeight,
              bgcolor: '#03111E',
              color: 'common.white',
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <Toolbar
              variant="dense"
              disableGutters
              sx={{
                minHeight: TOP_NAV_BAR_HEIGHT,
                maxHeight: TOP_NAV_BAR_HEIGHT,
                px: { xs: 1, sm: 1.5 },
                gap: 0.5,
                flexWrap: 'nowrap',
                alignItems: 'stretch',
                boxSizing: 'border-box',
              }}
            >
              {location.pathname !== '/pathfinder' && (
                <IconButton
                  color="inherit"
                  edge="start"
                  size="small"
                  aria-label="open menu"
                  onClick={handleDrawerToggle}
                  sx={{ display: { xs: 'inline-flex', sm: 'none' }, alignSelf: 'center' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  display: { xs: 'inline', sm: 'none' },
                  flexShrink: 0,
                  maxWidth: '40vw',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                PATHFINDER
              </Typography>
              <Tabs
                value={navTabPath}
                onChange={(_, path: string) => navigate(path)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: TOP_NAV_BAR_HEIGHT,
                  display: { xs: 'none', sm: 'flex' },
                  '& .MuiTabs-flexContainer': { alignItems: 'center' },
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.72)',
                    minHeight: TOP_NAV_BAR_HEIGHT - 4,
                    maxHeight: TOP_NAV_BAR_HEIGHT - 4,
                    py: 0,
                    px: 1,
                    textTransform: 'none',
                  },
                  '& .MuiTab-iconWrapper': { mb: 0, mr: 0.5 },
                  '& .Mui-selected': { color: 'common.white !important' },
                  '& .MuiTabs-indicator': { bgcolor: 'primary.light' },
                }}
              >
                {menuItems.map((item) => (
                  <Tab
                    key={item.path}
                    label={item.text}
                    value={item.path}
                    icon={item.icon as React.ReactElement}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                  ml: { xs: 'auto', sm: 0 },
                }}
              >
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<BugReportIcon />}
                  onClick={openBugReportEmail}
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  Report
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.username}
                </Typography>
                <Button color="inherit" size="small" startIcon={<Logout />} onClick={handleLogout}>
                  Log out
                </Button>
              </Box>
            </Toolbar>
          </AppBar>

          <SwipeableDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            onOpen={handleDrawerToggle}
            disableBackdropTransition={false}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                width: 'min(88vw, 300px)',
                top: topChromeOffset,
                height: `calc(100% - ${topChromeOffset + bannerHeight}px)`,
                boxSizing: 'border-box',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, overflow: 'auto' }}>{mobileNavList}</Box>
              <Divider />
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<BugReportIcon />}
                  onClick={openBugReportEmail}
                >
                  Report Bug
                </Button>
                <Button size="small" variant="outlined" startIcon={<Logout />} onClick={handleLogout}>
                  Log out
                </Button>
              </Box>
            </Box>
          </SwipeableDrawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: '100%',
              maxWidth: '100%',
              /* Explicit height + box-sizing: border-box so percentage-height children
                 (e.g. PathfinderPage uses height: 100%) fill the inner area. Without this,
                 parent only has minHeight: 100vh and children collapse. Matches the
                 side-nav branch below which sets an explicit height on main. */
              height: '100vh',
              maxHeight: '100vh',
              overflow: 'auto',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              /* Padding (not margin) avoids extra flex gap below fixed AppBar */
              pt: `${topChromeOffset}px`,
              pb: `${bannerHeight}px`,
              backgroundColor: 'background.default',
            }}
          >
            <LayoutProvider onToggleMobileDrawer={handleDrawerToggle}>
              {children}
            </LayoutProvider>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', flex: 1, marginTop: `${bannerHeight}px` }}>
            {/* Mobile-only floating menu (Pathfinder uses its own menu) */}
            {location.pathname !== '/pathfinder' && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  position: 'fixed',
                  top: bannerHeight + 8,
                  left: 8,
                  zIndex: 1201,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box
              component="nav"
              sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
              aria-label="navigation menu"
            >
              <SwipeableDrawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                onOpen={handleDrawerToggle}
                disableBackdropTransition={false}
                disableDiscovery={false}
                swipeAreaWidth={20}
                ModalProps={{ keepMounted: true }}
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: { xs: '80vw', sm: DRAWER_WIDTH },
                    maxWidth: 280,
                    top: `${bannerHeight}px`,
                    height: `calc(100% - ${bannerHeight * 2}px)`,
                    bgcolor: 'background.paper',
                  },
                }}
              >
                {drawer}
              </SwipeableDrawer>
              <Drawer
                variant="permanent"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: currentDrawerWidth,
                    transition: 'width 0.3s',
                    overflowX: 'hidden',
                    top: `${bannerHeight}px`,
                    height: `calc(100% - ${bannerHeight * 2}px)`,
                  },
                }}
                open
              >
                {drawer}
              </Drawer>
            </Box>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
                backgroundColor: 'background.default',
                maxWidth: '100%',
                overflow: 'auto',
                overflowX: 'hidden',
                transition: 'width 0.3s, margin 0.3s',
                marginTop: 0,
                minHeight: `calc(100vh - ${bannerHeight * 2}px)`,
                height: `calc(100vh - ${bannerHeight * 2}px)`,
              }}
            >
              <LayoutProvider onToggleMobileDrawer={handleDrawerToggle}>
                {children}
              </LayoutProvider>
            </Box>
          </Box>
        </>
      )}

      {/* Bottom classification banner */}
      {bannerVisual && (
        <Box
          sx={{
            backgroundColor: bannerVisual.backgroundColor,
            color: bannerVisual.color,
            textAlign: 'center',
            py: 0.5,
            px: 2,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.modal + 2,
            height: CUI_BANNER_HEIGHT,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#fff' }}
          >
            {bannerVisual.label}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Layout;
